import { CSSProperties, KeyboardEvent, useRef, useState } from "react";
import { HOBBIES_LIMIT } from "../../lib/common/constants";

type TPillInput = {
    hook: TusePillInputReturn;
    style?: CSSProperties;
};

type TusePillInputReturn = {
    values: string[];
    addValue: (val: string) => void;
    removeValueAt: (idx: number) => void;
    setValues: React.Dispatch<React.SetStateAction<string[]>>;
    canAddMoreHobbies: boolean;
};

type TusePillInput = (initialValues: string[]) => TusePillInputReturn;

const DEFAULT_SUGGESTED_OPTIONS = [
    "Walking",
    "Running",
    "Yoga",
    "Painting",
    "Cooking",
    "Baking",
    "Gardening",
    "Hiking",
    "Photography",
    "Reading",
    "Travel",
    "Music",
    "Dancing",
    "Swimming",
    "Cycling",
    "Pilates",
    "Meditation",
    "Fitness",
    "Movies",
    "Crafting",
    "Volunteering",
    "Board Games",
    "Coffee",
    "Pets",
];

export const usePillInput: TusePillInput = (initialValues: string[] = []) => {
    const [values, setValues] = useState<string[]>([...initialValues]);
    const canAddMoreHobbiesByLength = (valuesLength: number) => valuesLength < HOBBIES_LIMIT;
    const [canAddMoreHobbies, setCanAddMoreHobbies] = useState<boolean>(canAddMoreHobbiesByLength(initialValues.length));

    const addValue = (newValue: string) => {
        setValues((before) => {
            if (before.length >= HOBBIES_LIMIT) {
                setCanAddMoreHobbies(false);
                return before;
            }
            const newValues = [...before, newValue];
            setCanAddMoreHobbies(canAddMoreHobbiesByLength(newValues.length));
            return newValues;
        });
    };

    const removeValueAt = (idx: number) => {
        setValues((old) => {
            old.splice(idx, 1);
            const newValues = [...old];
            setCanAddMoreHobbies(canAddMoreHobbiesByLength(newValues.length));
            return newValues;
        });
    };

    return {
        values,
        addValue,
        removeValueAt,
        setValues,
        canAddMoreHobbies,
    };
};

export const PillInput = ({ hook }: TPillInput) => {
    const { values, addValue, removeValueAt, canAddMoreHobbies } = hook;
    const inputRef = useRef<HTMLInputElement>(null);
    const suggestedOptions = DEFAULT_SUGGESTED_OPTIONS;

    const keyDownHandler = (e: KeyboardEvent) => {
        e.stopPropagation();
        const target = e.target as HTMLInputElement;

        if (e.key === "Backspace") {
            if (target.value) return;

            if (!values.length) return;

            removeValueAt(values.length - 1);
            return;
        }

        if (e.key !== "Enter" && e.key !== ",") return;

        e.preventDefault();
        addValuesHandler();
    };

    const addValuesHandler = () => {
        if (!inputRef.current || !inputRef.current.value.trim()) return;

        inputRef.current.value
            .split(",")
            .map((pill) => pill.trim())
            .filter(Boolean)
            .forEach((pill) => {
                if (!values.some((value) => value.toLocaleLowerCase() === pill.toLocaleLowerCase())) {
                    addValue(pill);
                }
            });

        inputRef.current.value = "";
    };

    const suggestedOptionClickHandler = (isSelected: boolean, selectedIndex: number, option: string) => {
        if (isSelected) {
            removeValueAt(selectedIndex);
            return;
        }

        addValue(option);
    };

    const suggestedOptionPill = suggestedOptions.map((option) => {
        const selectedIndex = values.findIndex((value) => value.toLocaleLowerCase() === option.toLocaleLowerCase());
        const isSelected = selectedIndex !== -1;

        return (
            <button
                key={option}
                type="button"
                className={`btn btn-sm rounded-pill px-3 py-1 ${isSelected ? "btn-primary text-white" : "btn-light border"}`}
                onClick={() => suggestedOptionClickHandler(isSelected, selectedIndex, option)}
                disabled={!isSelected && !canAddMoreHobbies}
            >
                {option}
            </button>
        );
    });

    const customPill = values
        .filter((value) => !suggestedOptions.some((option) => option.toLocaleLowerCase() === value.toLocaleLowerCase()))
        .map((value, idx) => {
            const valueIndex = values.indexOf(value);

            return (
                <span
                    key={`${value}-${idx}`}
                    className="badge rounded-pill bg-primary text-white d-inline-flex align-items-center px-3 py-2 fw-normal text-wrap text-break"
                    data-testid="pill"
                >
                    <span>{value}</span>
                    <button
                        type="button"
                        className="btn btn-sm text-white border-0 p-0 ms-2 lh-1"
                        onClick={() => removeValueAt(valueIndex)}
                        aria-label={`Remove ${value}`}
                    >
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                </span>
            );
        });

    return (
        <div data-testid="container">
            <div className="d-flex flex-wrap gap-2">
                {suggestedOptionPill}
                {customPill}
            </div>

            <div className="mt-4">
                <p className="text-muted small mb-2">Don&apos;t see yours? Add your own custom tag:</p>
                <div className="input-group">
                    <input
                        data-testid="input-area"
                        ref={inputRef}
                        className="form-control form-control-md border-end-0"
                        onKeyDown={keyDownHandler}
                        maxLength={25}
                        placeholder="Type another hobby or interest and press Enter or (+)..."
                        disabled={!canAddMoreHobbies}
                    />
                    <button
                        className="btn btn-sm btn-outline-primary border-start-0 px-4 px-md-5 rounded-end"
                        onClick={addValuesHandler}
                        type="button"
                        disabled={!canAddMoreHobbies}
                        aria-label="Add custom interest"
                    >
                        <i className="fa-solid fa-plus"></i>
                    </button>
                </div>
                {!canAddMoreHobbies && <small className="text-secondary d-block mt-2">You&apos;ve selected 10 of 10. Remove one to add another.</small>}
            </div>
        </div>
    );
};
